import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { clientsApi, invoicesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SatProductSearch, SatUnitSearch } from "@/components/ui/sat-search";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { value: "PUE", label: "Pago en Una sola Exhibición (PUE)" },
  { value: "PPD", label: "Pago en Parcialidades o Diferido (PPD)" }
];

const PAYMENT_FORMS = [
  { value: "01", label: "01 - Efectivo" },
  { value: "02", label: "02 - Cheque nominativo" },
  { value: "03", label: "03 - Transferencia electrónica" },
  { value: "04", label: "04 - Tarjeta de crédito" },
  { value: "28", label: "28 - Tarjeta de débito" },
  { value: "99", label: "99 - Por definir" }
];

const CreateInvoicePage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  
  const [formData, setFormData] = useState({
    client_id: "",
    payment_method: "PUE",
    payment_form: "03",
    currency: "MXN",
    notes: "",
    items: [{ 
      description: "", 
      quantity: 1, 
      unit_price: 0, 
      unit: "PZA", 
      clave_producto: "01010101", 
      clave_unidad: "H87" 
    }]
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsApi.list();
      setClients(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { 
        description: "", 
        quantity: 1, 
        unit_price: 0, 
        unit: "PZA", 
        clave_producto: "01010101", 
        clave_unidad: "H87" 
      }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error("Debe haber al menos un concepto");
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0
    );
    const iva = subtotal * 0.16;
    const total = subtotal + iva;
    return { subtotal, iva, total };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const handleSubmit = async (issue = false) => {
    if (!formData.client_id) {
      toast.error("Selecciona un cliente");
      return;
    }

    const validItems = formData.items.filter(item => 
      item.description && item.quantity > 0 && item.unit_price > 0
    );

    if (validItems.length === 0) {
      toast.error("Agrega al menos un concepto válido");
      return;
    }

    // Validar que todos tengan clave producto
    for (const item of validItems) {
      if (!item.clave_producto) {
        toast.error("Todos los conceptos deben tener una clave de producto SAT");
        return;
      }
    }

    setLoading(true);

    try {
      const invoiceData = {
        ...formData,
        items: validItems.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      const response = await invoicesApi.create(invoiceData);
      
      if (issue) {
        await invoicesApi.issue(response.data.id);
        toast.success("Factura creada y timbrada exitosamente");
      } else {
        toast.success("Factura guardada como borrador");
      }
      
      navigate("/dashboard/invoices");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear factura");
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, iva, total } = calculateTotals();
  const selectedClient = clients.find(c => c.id === formData.client_id);

  return (
    <div className="space-y-6" data-testid="create-invoice-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Nueva Factura</h1>
          <p className="text-slate-600">Crea una nueva factura electrónica CFDI 4.0</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client selection */}
          <Card className="cia-card">
            <CardHeader>
              <CardTitle className="text-lg">Cliente</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#5B9BD5]" />
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">No tienes clientes registrados</p>
                  <Button variant="outline" onClick={() => navigate("/dashboard/clients")} data-testid="add-client-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Cliente
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Seleccionar Cliente</Label>
                    <Select 
                      value={formData.client_id} 
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                    >
                      <SelectTrigger data-testid="client-select">
                        <SelectValue placeholder="Buscar cliente..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} - {client.rfc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedClient && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="font-medium text-slate-900">{selectedClient.razon_social}</p>
                      <p className="text-sm text-slate-600">RFC: {selectedClient.rfc}</p>
                      <p className="text-sm text-slate-600">Email: {selectedClient.email}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice items with SAT catalog */}
          <Card className="cia-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Conceptos</CardTitle>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.items.map((item, index) => (
                <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-4" data-testid={`invoice-item-${index}`}>
                  <div className="flex items-start justify-between">
                    <span className="text-sm font-medium text-slate-500">Concepto {index + 1}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* SAT Product Code */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Clave Producto SAT *</Label>
                      <SatProductSearch
                        value={item.clave_producto}
                        onChange={(clave) => handleItemChange(index, "clave_producto", clave)}
                        onDescriptionSelect={(desc) => {
                          if (!item.description) {
                            handleItemChange(index, "description", desc);
                          }
                        }}
                      />
                      {item.clave_producto === "01010101" && (
                        <p className="text-xs text-orange-600">No existe en el catálogo</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Clave Unidad SAT *</Label>
                      <SatUnitSearch
                        value={item.clave_unidad}
                        onChange={(clave) => handleItemChange(index, "clave_unidad", clave)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => handleItemChange(index, "description", e.target.value)}
                      placeholder="Descripción del producto o servicio"
                      className="cia-input"
                      data-testid={`item-description-${index}`}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Cantidad</Label>
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                        className="cia-input"
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidad (texto)</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                        className="cia-input"
                        placeholder="PZA, SRV, etc."
                        data-testid={`item-unit-${index}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Precio Unitario</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, "unit_price", e.target.value)}
                        className="cia-input"
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                  </div>

                  <div className="text-right text-sm">
                    <span className="text-slate-500">Importe: </span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Payment details */}
          <Card className="cia-card">
            <CardHeader>
              <CardTitle className="text-lg">Datos de Pago</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Método de Pago</Label>
                  <Select 
                    value={formData.payment_method} 
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Forma de Pago</Label>
                  <Select 
                    value={formData.payment_form} 
                    onValueChange={(value) => setFormData({ ...formData, payment_form: value })}
                  >
                    <SelectTrigger data-testid="payment-form-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_FORMS.map((form) => (
                        <SelectItem key={form.value} value={form.value}>
                          {form.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionales para la factura..."
                  className="cia-input"
                  data-testid="notes-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary sidebar */}
        <div className="space-y-6">
          <Card className="cia-card sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg">Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">IVA (16%):</span>
                  <span className="font-medium">{formatCurrency(iva)}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-slate-900">Total:</span>
                    <span className="text-lg font-bold text-[#5B9BD5]">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <Button 
                  className="w-full cia-btn-primary"
                  onClick={() => handleSubmit(true)}
                  disabled={loading}
                  data-testid="issue-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Timbrar Factura
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => handleSubmit(false)}
                  disabled={loading}
                  data-testid="save-draft-btn"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Borrador
                </Button>
              </div>

              <div className="pt-4 border-t">
                <p className="text-xs text-slate-500 text-center">
                  Al timbrar, se generará el CFDI con los datos proporcionados.
                  Verifica que toda la información sea correcta.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoicePage;
